import React, {
  JSX,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Tooltip } from "@/components/common/Tooltip";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

export function EditableName(props: {
  id?: string;
  name: string;
  isEditing: boolean;
  canEdit?: boolean;
  setIsEditing: (isEditing: boolean) => void;
  update: (current: string) => void;
  commit: (name: string) => boolean | void;
  abort: () => void;
  editColor?: string;
  /** "icon": only the hover pencil enters edit mode — name clicks pass through. */
  editTrigger?: "click" | "doubleClick" | "icon";
  emptyPlaceholder?: string;
  /** Accessible name for the input while editing. */
  ariaLabel?: string;
  maxLength?: number;
  /** What leaving the field does; "abort" for a name that would create something. */
  blurAction?: "commit" | "abort";
  /** False drops the hover pencil, for a host that says "editable" another way. */
  editIcon?: boolean;
}): JSX.Element {
  const [currentName, setCurrentName] = useState<string>(props.name);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);
  const skipBlurCommitRef = useRef<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textMeasurerRef = useRef<HTMLSpanElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const nameDisplayRef = useRef<HTMLDivElement>(null);
  const {
    canEdit = false,
    editTrigger = "doubleClick",
    editIcon = true,
  } = props;
  const initialName = props.name;

  useEffect(() => {
    setCurrentName(props.name);
  }, [props.name]);

  // Before paint, not after: a passive effect lets the input show at its
  // intrinsic ~20-character width for a frame, which clips any longer name.
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (textMeasurerRef.current && inputRef.current && parentRef.current) {
        const rect = textMeasurerRef.current.getBoundingClientRect();
        const textWidth = rect.width + 2;
        parentRef.current.style.width = `${textWidth}px`;
        const input = inputRef.current;
        // Only when the whole name fits — clamped at max-width, snapping back
        // to the start would hide the caret on every keystroke.
        if (input.scrollWidth <= input.clientWidth) input.scrollLeft = 0;
      }
    };

    if (props.isEditing) {
      updateWidth();
      if (parentRef.current) {
        parentRef.current.style.maxWidth = "100%";
      }
    } else {
      if (parentRef.current) {
        parentRef.current.style.width = "";
        parentRef.current.style.maxWidth = "";
      }
    }
  }, [currentName, props.isEditing]);

  // After the sizing effect above, so the caret lands against the final width.
  useLayoutEffect(() => {
    if (props.isEditing && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      const textLength = input.value.length;
      input.setSelectionRange(textLength, textLength);
    }
  }, [props.isEditing]);

  useEffect(() => {
    const checkTruncation = () => {
      if (nameDisplayRef.current && !props.isEditing) {
        const textSpan = nameDisplayRef.current.querySelector(
          "span",
        ) as HTMLElement | null;
        const element = textSpan || nameDisplayRef.current;
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [currentName, props.isEditing]);

  const handleNameClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (canEdit) {
      props.setIsEditing(true);
    }
  };

  const abortEdit = () => {
    props.abort();
    setCurrentName(initialName);
    props.setIsEditing(false);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event,
  ) => {
    event.stopPropagation();
    if (["Enter"].includes(event.key)) {
      const accepted = props.commit(currentName);
      if (accepted === false) return;
      skipBlurCommitRef.current = true;
      props.setIsEditing(false);
    } else if (["Escape", "Esc"].includes(event.key)) {
      skipBlurCommitRef.current = true;
      abortEdit();
    }
  };

  const handleBlur = () => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    if (props.blurAction === "abort") return abortEdit();
    const accepted = props.commit(currentName);
    if (accepted === false) setCurrentName(initialName);
    props.setIsEditing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.update(e.target.value);
    setCurrentName(e.target.value);
  };

  return (
    <div
      id={props.id}
      ref={parentRef}
      className="inline-block me-0.5 max-w-full min-h-[1.5em]"
      onClick={(e) => {
        if (props.isEditing) {
          e.stopPropagation();
        }
      }}
      onDoubleClick={(e) => {
        if (props.isEditing) {
          e.stopPropagation();
        }
      }}
    >
      <span
        ref={textMeasurerRef}
        className={`invisible absolute top-0 left-0 -z-10 p-0 m-0 font-inherit text-inherit whitespace-pre ${
          canEdit ? "cursor-text" : "cursor-default"
        }`}
      >
        {currentName || "\u00A0"}
      </span>
      {props.isEditing ? (
        <input
          type="text"
          spellCheck={false}
          aria-label={props.ariaLabel}
          maxLength={props.maxLength}
          ref={inputRef}
          value={currentName}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="w-full outline-none p-0 m-0 font-inherit whitespace-pre leading-normal"
          style={{ color: props.editColor || "var(--color-accent)" }}
        />
      ) : (
        <Tooltip title={isTruncated ? currentName : ""}>
          <div
            ref={nameDisplayRef}
            onClick={editTrigger === "click" ? handleNameClick : undefined}
            onDoubleClick={
              editTrigger === "doubleClick"
                ? handleNameClick
                : (e) => {
                    if (canEdit) e.stopPropagation();
                  }
            }
            className={`group/name font-inherit text-inherit whitespace-pre select-none p-0 m-0 leading-normal inline-flex items-center gap-1 max-w-full ${
              canEdit && editTrigger === "click" ? "cursor-text" : ""
            } ${!currentName ? "min-w-16 bg-transparent" : ""}`}
          >
            <span className="truncate">
              {currentName || props.emptyPlaceholder || "\u00A0"}
            </span>
            {canEdit && editTrigger === "click" && editIcon && (
              <EditOutlinedIcon
                className="shrink-0 opacity-0 group-hover/name:opacity-70 transition-opacity duration-100"
                sx={{ fontSize: 12, color: "var(--color-fg-muted)" }}
              />
            )}
            {canEdit && editTrigger === "icon" && (
              // No room until the name is hovered, so a row's next item sits
              // right after the name rather than past an invisible pencil.
              <span
                onClick={handleNameClick}
                className="flex items-center shrink-0 cursor-pointer w-0 -ml-1 overflow-hidden opacity-0 group-hover/name:w-3 group-hover/name:ml-0 group-hover/name:opacity-70 hover:opacity-100 transition-[width,margin,opacity] duration-100"
                aria-label="Rename"
              >
                <EditOutlinedIcon
                  sx={{ fontSize: 12, color: "var(--color-fg-muted)" }}
                />
              </span>
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
}
